/**
 * API client for clause flagging / clause review endpoints.
 */
import apiClient from '../lib/apiClient';
import type { ClauseReviewSummary, ClauseFlagStatus, ClauseFlagLane, ClauseFlagLocateResult, ClauseRiskSummary, RiskPreferences } from '@corner/shared';
import { notifyClauseFlagsUpdated } from '../components/app/IssuesPanel';

export interface CreateClauseReviewInput {
  sourceKind: 'file' | 'document';
  sourceId: string;
  title?: string;
  jurisdiction?: string;
  contractType?: string;
  lane?: ClauseFlagLane;
}

export async function createClauseReview(input: CreateClauseReviewInput): Promise<ClauseReviewSummary> {
  const { data } = await apiClient.post<ClauseReviewSummary>('/api/clause-reviews', input);
  // A fresh review almost always produces new pending flags — let the
  // project Issues tab refetch so its count and inbox include them
  // immediately, not after the next manual tab switch.
  notifyClauseFlagsUpdated();
  return data;
}

export async function getClauseReview(clauseReviewId: string): Promise<ClauseReviewSummary> {
  const { data } = await apiClient.get<ClauseReviewSummary>(`/api/clause-reviews/${clauseReviewId}`);
  return data;
}

export async function patchClauseFlag(
  clauseReviewId: string,
  flagId: string,
  status: ClauseFlagStatus,
): Promise<ClauseReviewSummary> {
  const { data } = await apiClient.patch<ClauseReviewSummary>(
    `/api/clause-reviews/${clauseReviewId}/flags/${flagId}`,
    { status },
  );
  // Tell the project Issues tab to refresh its open-flag count and
  // its inbox. Decoupled via a window event so this client doesn't
  // need to know whether the panel is even mounted (it might not be —
  // the user could be on the Documents tab when they accept a flag).
  notifyClauseFlagsUpdated();
  return data;
}

export async function locateClauseFlag(
  clauseReviewId: string,
  flagId: string,
): Promise<ClauseFlagLocateResult> {
  const { data } = await apiClient.get<ClauseFlagLocateResult>(
    `/api/clause-reviews/${clauseReviewId}/flags/${flagId}/locate`,
  );
  return data;
}

export async function getRiskSummary(sourceId: string): Promise<ClauseRiskSummary | null> {
  const { data } = await apiClient.get<ClauseRiskSummary | null>(
    `/api/clause-reviews/risk-summary/${sourceId}`,
  );
  return data;
}

export async function getRiskSummariesBatch(
  sourceIds: string[],
): Promise<Record<string, ClauseRiskSummary>> {
  if (sourceIds.length === 0) return {};
  const { data } = await apiClient.post<Record<string, ClauseRiskSummary>>(
    '/api/clause-reviews/risk-summaries-batch',
    { sourceIds },
  );
  return data;
}

export async function getRiskPreferences(): Promise<RiskPreferences> {
  const { data } = await apiClient.get<RiskPreferences>('/api/clause-reviews/risk-preferences');
  return data;
}

export async function updateRiskPreferences(prefs: Partial<RiskPreferences>): Promise<RiskPreferences> {
  const { data } = await apiClient.patch<RiskPreferences>('/api/clause-reviews/risk-preferences', prefs);
  return data;
}
